// src/hooks/useLocks.js
import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

// Scope localStorage key to the logged-in user so locks never leak between accounts
const getLsKey = (user) => user ? `sh_locks_${user.id}` : null;

const readLS = (key) => {
  if (!key) return {};
  try { return JSON.parse(localStorage.getItem(key) || '{}'); }
  catch { return {}; }
};

const writeLS = (key, data) => {
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(data));
};

export function useLocks(user) {
  const lsKey = getLsKey(user);
  const [locks, setLocks] = useState(() => readLS(lsKey));
  const [now, setNow] = useState(Date.now());

  // Tick every second for countdowns
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // When user changes (login/logout), reload locks from the correct key
  useEffect(() => {
    setLocks(readLS(lsKey));
  }, [lsKey]);

  // Sync from backend when user logs in
  useEffect(() => {
    if (!user) return;
    api.myLocks().then((redemptions) => {
      const fresh = {};
      redemptions.forEach(r => {
        fresh[r.couponId] = {
          lockedUntil:   r.lockedUntil,
          cooldownUntil: r.cooldownUntil,
          redeemedAt:    r.redeemedAt,
        };
      });
      setLocks(prev => {
        const merged = { ...prev, ...fresh };
        writeLS(lsKey, merged);
        return merged;
      });
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  // Lock a coupon
  const lockCoupon = useCallback(async (couponId) => {
    const result = await api.redeemCoupon(couponId);
    const entry = {
      lockedUntil:   result.redemption.lockedUntil,
      cooldownUntil: result.redemption.cooldownUntil,
      redeemedAt:    result.redemption.redeemedAt,
    };
    setLocks(prev => {
      const next = { ...prev, [couponId]: entry };
      writeLS(lsKey, next);
      return next;
    });
    return entry;
  }, [lsKey]);

  // Get status of a lock
  const getLockStatus = useCallback((couponId) => {
    // If no user, never show as locked
    if (!user) return { status: 'none' };

    const entry = locks[couponId];
    if (!entry) return { status: 'none' };

    const lockedUntil   = new Date(entry.lockedUntil).getTime();
    const cooldownUntil = new Date(entry.cooldownUntil).getTime();

    if (now < lockedUntil) {
      const msLeft = lockedUntil - now;
      const h = Math.floor(msLeft / 3600000);
      const m = Math.floor((msLeft % 3600000) / 60000);
      const s = Math.floor((msLeft % 60000) / 1000);
      const countdown = h > 0
        ? `${h}h ${String(m).padStart(2,'0')}m`
        : `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      return { status: 'locked', countdown, msLeft, lockedUntil, cooldownUntil };
    }

    if (now < cooldownUntil) {
      const msLeft = cooldownUntil - now;
      const h = Math.ceil(msLeft / 3600000);
      return { status: 'cooldown', hoursLeft: h, cooldownUntil };
    }

    return { status: 'eligible' };
  }, [locks, now, user]);

  return { locks, lockCoupon, getLockStatus };
}
